import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarCheckIcon, SpinnerGapIcon, XLogoIcon, PaperPlaneTiltIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";
import { fetchStoryScheduler, setStoryScheduler, schedulerPostNow } from "../lib/api";
import { Switch } from "./ui/switch";

const ago = (ts) => {
  if (!ts) return "";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function StoryScheduler() {
  const qc = useQueryClient();
  const [posting, setPosting] = useState(false);
  const q = useQuery({ queryKey: ["story-scheduler"], queryFn: fetchStoryScheduler, refetchInterval: 60000 });
  const d = q.data;
  if (!d) return null;

  const toggle = async (v) => {
    try {
      await setStoryScheduler({ enabled: v });
      qc.invalidateQueries({ queryKey: ["story-scheduler"] });
      toast.success(v ? `Auto-post ON — daily at ${String(d.hourUtc).padStart(2, "0")}:00 UTC` : "Auto-post OFF");
    } catch {
      toast.error("Couldn't update the scheduler");
    }
  };

  const postNow = async () => {
    if (!window.confirm(`Post "${d.next?.title || "the best unposted story"}" to X right now?`)) return;
    setPosting(true);
    toast.loading("Posting to X…", { id: "xpost" });
    try {
      const r = await schedulerPostNow();
      toast.success(`Posted${r.nTweets > 1 ? ` — ${r.nTweets}-tweet thread` : ""}`, { id: "xpost" });
      qc.invalidateQueries({ queryKey: ["story-scheduler"] });
      if (r.tweetUrl) window.open(r.tweetUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed", { id: "xpost" });
    } finally {
      setPosting(false);
    }
  };

  const lr = d.lastResult;
  return (
    <div className="border border-white/10 bg-black/30 p-5 sm:p-6 space-y-3" data-testid="story-scheduler-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <CalendarCheckIcon size={20} weight="fill" className="text-neon" />
          <h3 className="font-heading font-black text-lg tracking-tight">X Auto-Post</h3>
        </div>
        <span className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground/70">
          Daily at {String(d.hourUtc).padStart(2, "0")}:00 UTC · best unposted story
        </span>
        <div className="ml-auto flex items-center gap-2">
          <span className={`text-xs font-semibold ${d.enabled ? "text-neon" : "text-muted-foreground"}`}>{d.enabled ? "ON" : "OFF"}</span>
          <Switch checked={d.enabled} onCheckedChange={toggle} disabled={!d.configured} data-testid="scheduler-toggle" />
        </div>
      </div>

      {!d.configured && (
        <p className="text-xs text-amber-300/90" data-testid="scheduler-unconfigured">X posting keys missing — add consumer + access keys to enable.</p>
      )}

      <div className="flex flex-wrap items-center gap-3 text-sm">
        {d.next ? (
          <div className="flex items-center gap-2 min-w-0" data-testid="scheduler-next">
            <span className="text-[11px] font-mono uppercase text-muted-foreground/70 shrink-0">Next up</span>
            {d.next.symbol && <span className="px-1.5 py-0.5 text-[11px] font-mono border border-neon/30 text-neon shrink-0">${String(d.next.symbol).toUpperCase()}</span>}
            <span className="truncate text-white/90">{d.next.title}</span>
            <span className="text-[11px] text-muted-foreground shrink-0">{d.next.mode === "thread" ? `🧵 ${d.next.nTweets} tweets` : "single tweet"}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid="scheduler-empty">Library is fully posted — write a new story to queue one.</span>
        )}
        <button
          data-testid="scheduler-post-now-btn"
          onClick={postNow}
          disabled={posting || !d.configured || !d.next}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-neon/40 text-neon hover:bg-neon/[0.08] transition-colors disabled:opacity-50"
        >
          {posting ? <SpinnerGapIcon size={14} className="animate-spin" /> : <PaperPlaneTiltIcon size={14} weight="fill" />} Post now
        </button>
      </div>

      {lr && (
        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2 pt-2 border-t border-white/5" data-testid="scheduler-last-result">
          <XLogoIcon size={13} className={lr.ok ? "text-neon" : "text-red-400"} />
          {lr.ok ? (
            <>
              <span>Last post: <span className="text-white/80">{lr.title}</span> · {ago(lr.ts)}</span>
              {lr.tweetUrl && (
                <a href={lr.tweetUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-neon hover:underline">
                  View on X <ArrowSquareOutIcon size={12} />
                </a>
              )}
            </>
          ) : (
            <span className="text-red-300/90">Last attempt failed · {ago(lr.ts)} — {lr.error}</span>
          )}
        </div>
      )}
    </div>
  );
}
